const DEFAULT_TIMEOUT_MS = 900;

function callStorage(method, args, timeoutMs, transform = value => value) {
  return new Promise(resolve => {
    let settled = false;
    const finish = value => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    try {
      method(...args, (error, value, canRestore) => {
        if (error) finish(null);
        else finish(transform(value, canRestore));
      });
    } catch {
      finish(null);
    }
  });
}

function browserStore(storage) {
  return {
    async get(key) {
      try { return storage?.getItem?.(key) ?? null; } catch { return null; }
    },
    async set(key, value) {
      try { storage?.setItem?.(key, value); return true; } catch { return false; }
    },
    async remove(key) {
      try { storage?.removeItem?.(key); return true; } catch { return false; }
    },
  };
}

export function createTelegramStorage({
  webApp,
  deviceFallback = globalThis.localStorage,
  secureFallback = globalThis.sessionStorage,
  timeoutMs = DEFAULT_TIMEOUT_MS,
} = {}) {
  const supported = typeof webApp?.isVersionAtLeast !== 'function' || webApp.isVersionAtLeast('9.0');
  const deviceApi = supported ? webApp?.DeviceStorage : null;
  const secureApi = supported ? webApp?.SecureStorage : null;
  const fallbackDevice = browserStore(deviceFallback);
  // Sensitive fallback is session-only. It is never written to localStorage.
  const fallbackSecure = browserStore(secureFallback);
  const writes = new Map();
  const signatures = new Map();

  function serialize(scope, key, task) {
    const id = `${scope}:${key}`;
    const previous = writes.get(id) || Promise.resolve();
    const next = previous.catch(() => {}).then(task).finally(() => {
      if (writes.get(id) === next) writes.delete(id);
    });
    writes.set(id, next);
    return next;
  }

  const device = {
    available: Boolean(deviceApi),
    async get(key) {
      if (deviceApi?.getItem) {
        const value = await callStorage(deviceApi.getItem.bind(deviceApi), [key], timeoutMs);
        if (value !== null) return value;
      }
      return fallbackDevice.get(key);
    },
    async set(key, value) {
      const text = String(value ?? '');
      if (signatures.get(`device:${key}`) === text) return true;
      return serialize('device', key, async () => {
        if (signatures.get(`device:${key}`) === text) return true;
        let saved = null;
        if (deviceApi?.setItem) {
          saved = await callStorage(deviceApi.setItem.bind(deviceApi), [key, text], timeoutMs, value => value !== false);
        }
        if (saved !== true) saved = await fallbackDevice.set(key, text);
        if (saved) signatures.set(`device:${key}`, text);
        return Boolean(saved);
      });
    },
    async remove(key) {
      signatures.delete(`device:${key}`);
      return serialize('device', key, async () => {
        let removed = null;
        if (deviceApi?.removeItem) {
          removed = await callStorage(deviceApi.removeItem.bind(deviceApi), [key], timeoutMs, value => value !== false);
        }
        const fallbackRemoved = await fallbackDevice.remove(key);
        return removed === true || fallbackRemoved;
      });
    },
  };

  const secure = {
    available: Boolean(secureApi),
    async get(key) {
      if (secureApi?.getItem) {
        const result = await callStorage(
          secureApi.getItem.bind(secureApi),
          [key],
          timeoutMs,
          (value, canRestore) => ({ value: value ?? null, canRestore: Boolean(canRestore) }),
        );
        if (result?.value != null) return result.value;
      }
      return fallbackSecure.get(key);
    },
    async set(key, value) {
      const text = String(value ?? '');
      if (signatures.get(`secure:${key}`) === text) return true;
      return serialize('secure', key, async () => {
        if (signatures.get(`secure:${key}`) === text) return true;
        let saved = null;
        if (secureApi?.setItem) {
          saved = await callStorage(secureApi.setItem.bind(secureApi), [key, text], timeoutMs, value => value !== false);
        }
        if (saved !== true) saved = await fallbackSecure.set(key, text);
        if (saved) signatures.set(`secure:${key}`, text);
        return Boolean(saved);
      });
    },
    async remove(key) {
      signatures.delete(`secure:${key}`);
      return serialize('secure', key, async () => {
        let removed = null;
        if (secureApi?.removeItem) {
          removed = await callStorage(secureApi.removeItem.bind(secureApi), [key], timeoutMs, value => value !== false);
        }
        const fallbackRemoved = await fallbackSecure.remove(key);
        return removed === true || fallbackRemoved;
      });
    },
  };

  return { device, secure, flush: () => Promise.allSettled([...writes.values()]) };
}
